"use client";
// ----- Components Import -----
import React, { useState } from "react";
import { Row, Col } from "antd";
import styled from "styled-components";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { Spacer, Spinner } from "../../styles/CommonElements";
import Button from "../../components/Button";
import { trackEvent } from "../../helpers/mixpanel";
import { APP_NAME } from "../../helpers/constants";

/** Dark marketing shell: match Home / HomeMain (black field + industrial grid). */
const CONTACT_BG = "#000000";
const CONTACT_CARD = "#000000";
const CONTACT_BORDER = "#1e293b";
const CONTACT_TEXT = "#f8fafc";
const CONTACT_MUTED = "#94a3b8";
const CONTACT_INPUT_BG = "#0f172a";
const CONTACT_FOCUS_RING = "rgba(255, 87, 34, 0.25)";
const CONTACT_ACCENT = "var(--color-action, #ff5722)";
const CONTACT_FINANCIAL = "var(--color-financial, #00c853)";
const CONTACT_FINANCIAL_DIM = "var(--color-financial-glow, rgba(0, 200, 83, 0.12))";

export default function ContactPage() {
  const { isMobile } = useBreakpoints();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!name.trim() || !email.trim() || !message.trim()) {
      alert("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      trackEvent("Contact Form Submitted", {
        name,
        email,
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Prepare the data payload - send empty strings for shopName and website
      const contactData = {
        name: name.trim(),
        email: email.trim(),
        shopName: "",
        website: "",
        message: message.trim(),
      };

      // Make API call to the contact form endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sendContactForm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Contact form submitted successfully:", result);

      setSuccessfullySubmitted(true);

      trackEvent("Contact Form Submission Success", {
        name,
        email,
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Reset form after success
      setTimeout(() => {
        setName("");
        setEmail("");
        setMessage("");
        setSuccessfullySubmitted(false);
      }, 7000);
    } catch (error) {
      console.error("Contact form submission error:", error);

      trackEvent("Contact Form Submission Error", {
        name,
        email,
        error_message: error.message,
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Show user-friendly error message
      const errorMessage = error.message.includes("fetch")
        ? "Unable to connect to our servers. Please check your internet connection and try again."
        : "There was an error submitting your message. Please try again or contact us directly.";

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <ContentWrapper>
        <Row
          justify="center"
          align="top"
          style={{ minHeight: "auto", padding: "1rem 0 2rem" }}
        >
          <Col xs={22} sm={20} md={16} lg={14} xl={12} style={{ zIndex: 2 }}>
            <FormCard>
              {!isSubmitting && successfullySubmitted && (
                <SuccessContainer>
                  <SuccessTitle>Thank you for your interest!</SuccessTitle>
                  <SuccessMessage>
                    Our team will reach out within 1 business day to answer your
                    questions and help you get started.
                  </SuccessMessage>
                </SuccessContainer>
              )}

              {isSubmitting && !successfullySubmitted && (
                <LoadingContainer>
                  <SpinnerContainer>
                    <Spinner />
                  </SpinnerContainer>
                  <LoadingText>Sending your message...</LoadingText>
                </LoadingContainer>
              )}

              {!isSubmitting && !successfullySubmitted && (
                <>
                  {/* Header Section */}
                  <HeaderSection>
                    <MainTitle>Contact Us</MainTitle>
                    <Subtitle>
                      Have questions about diagnoses, vetted bid searches, or
                      which plan fits your portfolio? Need help before you
                      dispatch a job? Our team is here to help.
                    </Subtitle>
                  </HeaderSection>

                  {/* Contact Form */}
                  <ContactForm onSubmit={handleSubmit}>
                    <FormRow gutter={[24, 24]}>
                      <FormCol xs={24} sm={12}>
                        <FormGroup>
                          <Label>
                            Full Name<RequiredAsterisk>*</RequiredAsterisk>
                          </Label>
                          <StyledInput
                            value={name}
                            type="text"
                            placeholder="Enter your full name"
                            maxLength={100}
                            onChange={(e) => setName(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </FormCol>

                      <FormCol xs={24} sm={12}>
                        <FormGroup>
                          <Label>
                            Email Address<RequiredAsterisk>*</RequiredAsterisk>
                          </Label>
                          <StyledInput
                            value={email}
                            type="email"
                            placeholder="you@email.com"
                            maxLength={100}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </FormCol>

                      <FormCol xs={24}>
                        <FormGroup>
                          <Label>
                            Tell us about your needs
                            <RequiredAsterisk>*</RequiredAsterisk>
                          </Label>
                          <StyledTextArea
                            value={message}
                            placeholder="Tell us about your repair or larger job, plans, partnerships, or anything else we should know."
                            rows={4}
                            maxLength={1000}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </FormCol>
                    </FormRow>

                    <SubmitButtonContainer>
                      <SubmitButton type="submit">Send Message</SubmitButton>
                      <ResponseTimeText>
                        <em>We typically respond within 1 business day.</em>
                      </ResponseTimeText>
                    </SubmitButtonContainer>
                  </ContactForm>
                </>
              )}
            </FormCard>
          </Col>
        </Row>
      </ContentWrapper>
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: ${CONTACT_BG};
  color: ${CONTACT_TEXT};
  position: relative;
  overflow-x: hidden;
  padding: 1rem 0;

  /* Same fixed mesh as Home.jsx - industrial “blueprint” field */
  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    opacity: 0.45;
    background-image: linear-gradient(
        rgba(10, 10, 10, 0.07) 0.5px,
        transparent 0.5px
      ),
      linear-gradient(90deg, rgba(10, 10, 10, 0.07) 0.5px, transparent 0.5px);
    background-size: 20px 20px;
  }

  /* Subtle brand wash (low contrast on black) */
  &::after {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background: radial-gradient(
      circle at 18% 0%,
      rgba(255, 87, 34, 0.06) 0%,
      transparent 42%
    );
  }

  @media ${device.mobile} {
    padding: 0.75rem 0;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  padding: 1rem 1.5rem;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const FormCard = styled.div`
  background: ${CONTACT_CARD};
  border-radius: 16px;
  padding: 2.5rem 3rem 2.25rem 3rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
  border: 1px solid ${CONTACT_BORDER};
  position: relative;
  z-index: 1;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${CONTACT_ACCENT},
      var(--color-financial-dark, #00b548)
    );
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem 1.75rem 1.5rem;
    border-radius: 12px;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const MainTitle = styled.h1`
  font-family: var(--font-primary);
  font-size: 3.25rem;
  font-weight: 600;
  color: ${CONTACT_TEXT};
  margin-bottom: 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.75rem;
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  color: ${CONTACT_MUTED};
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ContactForm = styled.form`
  width: 100%;
`;

const FormRow = styled(Row)`
  margin-bottom: 1rem;
`;

const FormCol = styled(Col)`
  margin-bottom: 1rem;
  vertical-align: top;
`;

const FormGroup = styled.div`
  width: 100%;
  min-height: 92px;
`;

const Label = styled.label`
  display: block;
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  font-weight: 600;
  color: ${CONTACT_TEXT};
  margin-bottom: 0.75rem;
  min-height: 24px;
  line-height: 1.2;
  letter-spacing: -0.005em;
`;

const RequiredAsterisk = styled.span`
  color: ${CONTACT_ACCENT};
  margin-left: 2px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid ${CONTACT_BORDER};
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: ${CONTACT_TEXT};
  background: ${CONTACT_INPUT_BG};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  margin-bottom: 0;
  vertical-align: top;

  &::placeholder {
    color: ${CONTACT_MUTED};
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: ${CONTACT_ACCENT};
    box-shadow: 0 0 0 3px ${CONTACT_FOCUS_RING};
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #475569;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 1rem;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 1.25rem 1.5rem;
  border: 2px solid ${CONTACT_BORDER};
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: ${CONTACT_TEXT};
  background: ${CONTACT_INPUT_BG};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  resize: vertical;
  min-height: 140px;

  &::placeholder {
    color: ${CONTACT_MUTED};
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: ${CONTACT_ACCENT};
    box-shadow: 0 0 0 3px ${CONTACT_FOCUS_RING};
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #475569;
  }

  @media (max-width: 768px) {
    padding: 1rem 1rem;
    font-size: 1rem;
  }
`;

const SubmitButtonContainer = styled.div`
  text-align: center;
  margin-top: 1.25rem;
`;

const SubmitButton = styled.button`
  background: ${CONTACT_ACCENT};
  color: #ffffff;
  border: 2px solid #000000;
  border-radius: 8px;
  padding: 1.25rem 3rem;
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.15s ease;
  box-shadow: 3px 3px 0 #000000;
  min-width: 240px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.18),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    background: #f44e1b;
    transform: translate(1px, 1px);
    box-shadow: 2px 2px 0 #000000;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px 0 #000000;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 2rem;
    font-size: 1.125rem;
    min-width: auto;
  }
`;

const ResponseTimeText = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: ${CONTACT_MUTED};
  margin-top: 1rem;
  margin-bottom: 0;
  font-style: italic;
  font-weight: 500;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: ${CONTACT_FINANCIAL_DIM};
  border-radius: 8px;
  border: 1px solid rgba(0, 200, 83, 0.35);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      rgba(0, 200, 83, 0.12) 0%,
      transparent 70%
    );
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SuccessTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2rem;
  font-weight: 600;
  color: ${CONTACT_FINANCIAL};
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
`;

const SuccessMessage = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  color: ${CONTACT_TEXT};
  line-height: 1.7;
  max-width: 450px;
  margin: 0 auto;
  font-weight: 500;
  letter-spacing: -0.005em;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    max-width: 100%;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const LoadingText = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: ${CONTACT_MUTED};
  margin-top: 1rem;
  margin-bottom: 0;
`;

const SpinnerContainer = styled.div`
  margin: auto;
  text-align: center;
  display: inline-flex;
  filter: invert(1) brightness(1.15);
`;
